import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/api";
import { PageTitle } from "@/components/ui-kit";
import { Delta } from "@/pages/MySchools";
import { UsersRound, Eye, AlertTriangle, Stethoscope, ClipboardCheck, Trophy, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/download";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

const Mini = ({ label, value, extra, tone = "text-sky-800" }) => (
  <div className="rounded-xl bg-sky-50/70 border border-sky-100 px-2 py-2 text-center">
    <div className={`font-bold text-sm ${tone}`}>{value}</div>
    <div className="text-[10px] text-stone-500">{label}</div>
    {extra}
  </div>
);

const SupCard = ({ s, rank }) => {
  const { user, impersonateSupervisor } = useAuth();
  const total = s.green + s.yellow + s.red || 1;
  const enter = async () => {
    try { await impersonateSupervisor(s.id); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid={`sup-card-${s.id}`} className="rounded-2xl bg-white/85 border border-amber-300/60 p-4 flex flex-col gap-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${rank <= 3 ? "bg-amber-400 text-stone-900" : "bg-stone-200 text-stone-700"}`}>{rank}</span>
          <div className="font-semibold text-stone-800 truncate">{s.name}</div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Link to={`/supervisors-oversight/${s.id}`} data-testid={`sup-open-${s.id}`} className="px-3 py-1.5 rounded-xl text-xs border border-sky-400/60 text-sky-800 bg-sky-50 flex items-center gap-1 hover:bg-sky-100"><Eye size={13} /> اطلاع</Link>
          {user?.is_monitor && <button data-testid={`sup-enter-${s.id}`} onClick={enter} className="px-3 py-1.5 rounded-xl text-xs bg-sky-600 text-white flex items-center gap-1 hover:bg-sky-700"><LogIn size={13} /> الدخول بحسابه</button>}
        </div>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-stone-100" title="منجزة / متوسطة / متأخرة">
        <div className="bg-emerald-500" style={{ width: `${(s.green / total) * 100}%` }} />
        <div className="bg-amber-400" style={{ width: `${(s.yellow / total) * 100}%` }} />
        <div className="bg-red-500" style={{ width: `${(s.red / total) * 100}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <Mini label="مدارسه" value={s.schools} />
        <Mini label="متوسط المشاركة" value={`${s.avg_rate}%`} extra={<Delta v={s.delta_rate} unit="%" />} />
        <Mini label="الساعات" value={s.hours} extra={<Delta v={s.delta_hours} />} />
        <Mini label="فرص منفذة" value={s.executed_opportunities} />
        <Mini label="التفعيل" value={`${s.activation_rate}%`} tone="text-emerald-700" />
        <Mini label="المتطوعون" value={s.volunteers} />
        <Mini label="متأخرة" value={s.red} tone="text-red-700" />
        <Mini label="بلا نشاط" value={s.alerts} tone="text-red-700" />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-stone-600">
        <span className="flex items-center gap-1"><ClipboardCheck size={12} /> المتابعات: {s.visits} (آخر 7 أيام: {s.visits_7d})</span>
        <span className="flex items-center gap-1"><Stethoscope size={12} /> خطط: {s.open_plans} مفتوحة / {s.closed_plans} مغلقة</span>
        {s.last_visit && <span>آخر متابعة: {s.last_visit}</span>}
      </div>
      {s.schools === 0 && <div className="text-[11px] text-red-700 flex items-center gap-1"><AlertTriangle size={12} /> لم تُربط به مدارس بعد</div>}
      {s.schools > 0 && s.visits_7d === 0 && <div className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle size={12} /> لا متابعات خلال آخر 7 أيام</div>}
    </div>
  );
};

export default function SupervisorsOversight() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/oversight/supervisors").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const ranked = [...(items || [])].sort((a, b) => b.avg_rate - a.avg_rate || b.hours - a.hours);
  const sum = (k) => (items || []).reduce((t, s) => t + (s[k] || 0), 0);

  return (
    <div className="space-y-5" data-testid="oversight-page">
      <PageTitle title="متابعة المشرفين" subtitle="قياس أثر المشرفين وإنجازهم مع مدارسهم — مرتبة حسب متوسط نسبة المشاركة" icon={UsersRound}
        actions={<div className="flex flex-wrap gap-2">
          <button data-testid="ov-report-pdf" onClick={() => downloadFile("/oversight/report?format=pdf", "تقرير-متابعة-المشرفين.pdf").catch(() => toast.error("تعذّر تنزيل التقرير"))} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-1"><FileDown size={15} /> تقرير المشرفين PDF</button>
          <button data-testid="ov-report-xlsx" onClick={() => downloadFile("/oversight/report?format=xlsx", "تقرير-متابعة-المشرفين.xlsx").catch(() => toast.error("تعذّر تنزيل التقرير"))} className="px-4 py-2 rounded-xl text-sm border border-emerald-500/50 text-emerald-700 bg-emerald-50 flex items-center gap-1"><FileDown size={15} /> Excel</button>
        </div>} />
      {items && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {[["المشرفون", items.length], ["مدارس مرتبطة", sum("schools")], ["الساعات", Math.round(sum("hours") * 10) / 10], ["مدارس متأخرة", sum("red")], ["المتابعات", sum("visits")], ["خطط مفتوحة", sum("open_plans")]].map(([l, v], i) => (
            <div key={l} data-testid={`ov-sum-${i}`} className="rounded-2xl bg-white/80 border border-sky-200 p-3"><div className="text-xl font-bold text-sky-800">{v}</div><div className="text-xs text-stone-500">{l}</div></div>
          ))}
        </div>
      )}
      {ranked[0] && ranked[0].schools > 0 && <div className="rounded-2xl bg-gradient-to-l from-amber-100 to-white border border-amber-300 px-4 py-3 text-sm flex items-center gap-2" data-testid="ov-top"><Trophy size={18} className="text-amber-600" /> الصدارة: <b>{ranked[0].name}</b> بمتوسط مشاركة {ranked[0].avg_rate}%</div>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {ranked.map((s, i) => <SupCard key={s.id} s={s} rank={i + 1} />)}
      </div>
      {items && !items.length && <div className="text-center text-stone-500 py-8">لا يوجد مشرفون بعد.</div>}
    </div>
  );
}
